// Zoom-reactive distance-marker density.
//
// Distance markers used to sit on a fixed 0.5-unit grid (labels every 1.0).
// Now they react to zoom: as the user zooms in, finer *unlabelled* markers
// fade in. Rather than accumulate (0.25 and 0.1 aren't multiples of each
// other, which would give uneven spacing), each zoom band shows one regular
// grid that replaces the coarser one:
//
//   baseline      -> 0.5-unit grid, labels on whole units (previous behaviour)
//   zoom >= 13    -> 0.25-unit grid (unlabelled)
//   zoom >= 15    -> 0.1-unit grid (unlabelled)
//
// Markers are computed once on a 0.05-unit base grid (the least common grid of
// 0.1 / 0.25 / 0.5) and tagged with the display grids they belong to; the
// Mapbox layer fades tiers in with an icon-opacity step(zoom) expression.

/** Base grid steps per full display unit (0.05 unit granularity). */
export const BASE_STEPS_PER_UNIT = 20;
export const TENTH_EVERY = 2; // 0.1 unit
export const QUARTER_EVERY = 5; // 0.25 unit
export const HALF_EVERY = 10; // 0.5 unit
export const FULL_EVERY = 20; // 1.0 unit (labelled)

/** Distance (base-grid units) between markers, i.e. 0.05 of a display unit. */
export const BASE_STEP_UNITS = 1 / BASE_STEPS_PER_UNIT;

/** Zoom thresholds at which finer tiers fade in. Tunable. */
export const QUARTER_MIN_ZOOM = 13;
export const TENTH_MIN_ZOOM = 15;

export interface MarkerTier {
  /** On the 0.5-unit grid (whole + half units). */
  onHalf: boolean;
  /** On the 0.25-unit grid (whole + half + quarter units). */
  onQuarter: boolean;
  /** On the 0.1-unit grid (tenths + halves + wholes, but not quarters). */
  onTenth: boolean;
  /** Whole-unit number to label (1, 2, 3, …), or null when unlabelled. */
  wholeUnit: number | null;
}

/**
 * Tier membership for a 1-based base-grid step, or `null` when the step sits on
 * no display grid (0.05, 0.15, 0.35, …) and should be skipped entirely.
 */
export function markerTierForStep(stepIndex: number): MarkerTier | null {
  const onTenth = stepIndex % TENTH_EVERY === 0;
  const onQuarter = stepIndex % QUARTER_EVERY === 0;
  if (!onTenth && !onQuarter) return null;
  return {
    onHalf: stepIndex % HALF_EVERY === 0,
    onQuarter,
    onTenth,
    wholeUnit: stepIndex % FULL_EVERY === 0 ? stepIndex / FULL_EVERY : null,
  };
}

/**
 * Mapbox `icon-opacity` expression: shows one regular grid per zoom band by
 * using the active band's per-feature 1/0 flag directly as the opacity. Below
 * QUARTER_MIN_ZOOM only the 0.5 grid shows; the 0.25 grid fades in at
 * QUARTER_MIN_ZOOM, the 0.1 grid at TENTH_MIN_ZOOM.
 */
export function iconOpacityZoomExpression(): unknown[] {
  return [
    'step',
    ['zoom'],
    ['get', 'onHalf'],
    QUARTER_MIN_ZOOM,
    ['get', 'onQuarter'],
    TENTH_MIN_ZOOM,
    ['get', 'onTenth'],
  ];
}
